import React, { useMemo, useEffect, useState } from "react"
import {useSession} from "next-auth/react";
import {useRouter} from "next/router";
import {filterLinksFromPermissions} from "../../utils/permissions";
import { Honeybadger } from '@honeybadger-io/react';

const AuthManager = ({isAuthRequired}) => {
    const router = useRouter();
    const {asPath} = router;

    // Keep the latest asPath available to the (stable) onUnauthenticated callback
    // without forcing the callback's identity to change on every render.
    const asPathRef = React.useRef(asPath);
    asPathRef.current = asPath;

    // Guard so the unauthenticated -> signin redirect only fires once. next-auth
    // re-runs its internal effect whenever onUnauthenticated's identity changes
    // (see node_modules/next-auth/react deps: [requiredAndNotLoading, onUnauthenticated]).
    // With the previous inline callback that meant a router.push to /session/signin
    // on *every* render while unauthenticated, racing concurrent navigations and
    // throwing "Invariant: attempted to hard navigate to the same URL".
    const redirectingRef = React.useRef(false);

    const onUnauthenticated = React.useCallback(() => {
        if (redirectingRef.current) return;
        const currentPath = asPathRef.current;
        // Already on (or heading to) the signin page: do nothing. Pushing to the
        // same URL triggers the hard-navigation invariant.
        if (currentPath.startsWith('/session/signin') || router.pathname.startsWith('/session/signin')) {
            return;
        }
        redirectingRef.current = true;
        router
            .push({pathname: '/session/signin', query: {redirectPath: currentPath}}, '/session/signin')
            .catch(() => {
                // Swallow benign navigation errors (e.g. the route being torn down
                // by a concurrent sign-out). The user still lands on signin, and
                // this keeps it from surfacing as an UnhandledPromiseRejection.
            });
    }, [router]);

    const {data: session, status} = useSession({
        required: isAuthRequired,
        onUnauthenticated,
    });

    // Re-arm the redirect guard once authenticated again. AuthManager is mounted
    // outside <Component> in _app.js, so it persists for the whole app session;
    // without this reset the ref would stay true after the first redirect and a
    // later session expiry would never redirect again.
    React.useEffect(() => {
        if (status === "authenticated") {
            redirectingRef.current = false;
        }
    }, [status]);

    // Only set Honeybadger context when session changes
    React.useEffect(() => {
        if (status === "authenticated" && session?.user) {
            Honeybadger.setContext({
                user: session.user
            });
        }
    }, [session?.user, status]);

    // Always call useMemo hooks regardless of conditions to maintain hook order
    // For protected routes validation
    const protectedRouteValidation = useMemo(() => {
        if (isAuthRequired && status === "authenticated" && session) {
            return validateProtectedRoute(session || {}, asPath);
        }
        return [true]; // Default value when conditions aren't met
    }, [isAuthRequired, status, session, asPath]);

    // For public route validation
    const publicRouteValidation = useMemo(() => {
        if (!isAuthRequired) {
            return validateRoute(asPath);
        }
        return [true]; // Default value when conditions aren't met
    }, [isAuthRequired, asPath]);

    // Determine redirect path based on validation results
    // Note: unauthenticated redirect is handled by onUnauthenticated callback above
    const redirectPath = useMemo(() => {
        if (isAuthRequired) {
            if (status === "authenticated" && session) {
                const [isValid, path] = protectedRouteValidation;
                if (!isValid && path) return path;
            }
            // Don't redirect here - onUnauthenticated callback handles this
        } else {
            const [isValid, path] = publicRouteValidation;
            if (status === "authenticated" && !isValid && path) {
                return path;
            }
        }
        return null;
    }, [isAuthRequired, status, session, protectedRouteValidation, publicRouteValidation]);

    // Handle redirects in useEffect to avoid state updates during render
    useEffect(() => {
        if (redirectPath) {
            router.push(redirectPath);
        }
    }, [redirectPath, router]);

    return <></>;
};

// Refactored to eliminate code duplication
const validateProtectedRoute = ({user, company}, asPath) => {
    // If no company (which shouldn't happen, but in the case it does,
    // it should sign the user out to prevent stuck state)
    if (!company) return [false, '/session/signout'];

    // If sign up is complete -> Deal with Approved vs Not Approved
    if (company.sign_up_complete) {
        // check to see if allow into app - same logic for both branches
        if (/^\/(signup|session)/.test(asPath)) {
            return [false, '/'];
        }

        // Check permissions - same logic for both branches
        const permissions = user?.permissions;
        const links = filterLinksFromPermissions(permissions);

        // If trying to access dashboard but don't have permission, redirect to next tab
        // The next tab is the first link with an href (skipping search which has onClick)
        // Handle dashboard path with or without query parameters or hash fragments
        if ((asPath === "/" || asPath === "" ) && !permissions.includes("dashboard") && links.length > 0) {
            // Find the first link that's not search (has an href property)
            const firstNonSearchLink = links.find(link => link.href);
            return [false, firstNonSearchLink?.href];
        }

        // redirect when trying to hit an asPath that user does not have permission to
        if (asPath !== "/account-details" && !links.some((link) => asPath.includes(link.href))) {
            // Find the first link that's not search (has an href property)
            const firstNonSearchLink = links.find(link => link.href);
            return [false, firstNonSearchLink?.href];
        }

    } else { // if sign up not complete, set user back the the stage left off
        // If Shopify is linked, then skip /signup/shop and go to /signup/business
        if (company.has_shopify_linked && (asPath === '/signup/shop')) {
            return [false, '/signup/business'];
        }

        // Always redirect to /signup/business unless
        // user was on /signup/shop (which makes it skippable)
        if ((asPath !== "/signup/shop") && (asPath !== "/signup/business")) {
            return [false, '/signup/business'];
        }
    }

    return [true];
};

// Optimized route validation with more efficient regex
const validateRoute = (asPath) => {
    /*
      If the user is going to a /session/* page redirect them to /
      If the user is going to /signup/blah page specifically redirect them to /
         exception /signup/pending and /signup/join-team
    */
    // Precompile regex for better performance
    const sessionRegex = /\/session\/(?!signin|signout).*/;
    const signupRegex = /\/signup\/.+/;
    const pendingRegex = /\/signup\/pending.*/;
    const joinTeamRegex = /\/signup\/join-team.*/;
    const approvedRegex = /^\/signup.*(approved=true|pending=true).*/;

    if ((sessionRegex.test(asPath)) || 
        (signupRegex.test(asPath) && !pendingRegex.test(asPath) && !joinTeamRegex.test(asPath))) {
        return [false, '/'];
    }

    //If the user is going to /signup?approved=true redirect them to /session/signin?source=shopify
    if (approvedRegex.test(asPath)) {
        return [false, '/session/signin?source=shopify'];
    }

    return [true];
}

// For debugging
AuthManager.displayName = 'AuthManager';

// Custom comparison function for React.memo to prevent unnecessary re-renders
// Only re-render if isAuthRequired changes
const areEqual = (prevProps, nextProps) => {
    return prevProps.isAuthRequired === nextProps.isAuthRequired;
};

// Export with custom comparison function
export default React.memo(AuthManager, areEqual);
